/**
 * Level Rewards Card Component
 * Shows all level rewards and progression
 */

import { Trophy } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LEVELS } from '../../../../stores/gamification-store';
import { formatXp } from '../utils/helpers';

interface LevelRewardsCardProps {
  currentLevel: number;
}

export function LevelRewardsCard({ currentLevel }: LevelRewardsCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Trophy className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('profile.levelRewards.title', 'Level Rewards')}
            </h2>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-3">
        {LEVELS.map((levelInfo) => {
          const isCurrentLevel = currentLevel === levelInfo.level;
          const isCompleted = currentLevel > levelInfo.level;

          return (
            <div
              key={levelInfo.level}
              className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                isCurrentLevel
                  ? 'bg-primary-50 border border-primary-200'
                  : isCompleted
                  ? 'bg-green-50'
                  : 'bg-gray-50'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isCurrentLevel
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {levelInfo.level}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">{levelInfo.title}</div>
                <div className="text-sm text-gray-500">
                  {formatXp(levelInfo.cumulativeXp)} {t('profile.levelRewards.xpRequired', 'XP required')}
                </div>
              </div>
              <div className="text-right text-sm flex-shrink-0">
                {levelInfo.badgeId && (
                  <div className="text-purple-600">+ {t('profile.badge', 'Badge')}</div>
                )}
                {levelInfo.watchlistSlotReward && (
                  <div className="text-green-600">+ {t('profile.watchlistSlot', 'Watchlist Slot')}</div>
                )}
                {levelInfo.premiumDaysReward && (
                  <div className="text-amber-600">
                    + {levelInfo.premiumDaysReward} {t('profile.proDay', 'Pro Day')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
